import { useState } from 'react';
import { Paperclip, X } from 'lucide-react';
import { FileUploader } from '@/components/file-uploader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

type AttachmentDialogProps = {
  /** Currently attached files */
  attachments: File[];
  /** Callback when attachments are updated */
  onAttachmentsChange: (files: File[]) => void;
  /** Optional custom trigger element */
  children?: React.ReactNode;
};

/**
 * Dialog for managing email attachments.
 * Allows users to add files via drag-and-drop or file picker.
 * Files are stored locally and only uploaded when the email is sent.
 */
export function AttachmentDialog({
  attachments,
  onAttachmentsChange,
  children,
}: AttachmentDialogProps) {
  const [open, setOpen] = useState(false);

  const handleFilesChange = (files: File[] | undefined) => {
    onAttachmentsChange(files ?? []);
  };

  const handleAddFiles = () => {
    setOpen(false);
  };

  const handleClearAll = () => {
    onAttachmentsChange([]);
  };

  const attachmentCount = attachments.length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children ?? (
          <Button
            variant="ghost"
            size="sm"
            className="relative size-8 p-0"
            title="Add attachments"
          >
            <Paperclip className="size-4" />
            {attachmentCount > 0 && (
              <Badge
                variant="secondary"
                className="absolute -right-1 -top-1 flex size-4 items-center justify-center p-0 text-[10px]"
              >
                {attachmentCount}
              </Badge>
            )}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Attach Files</DialogTitle>
          <DialogDescription>
            Add files to your email. Files will be uploaded when the email is sent.
            (Max 10 files, 25MB each)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <FileUploader
            value={attachments}
            onValueChange={handleFilesChange}
            maxFileCount={10}
            maxSize={25 * 1024 * 1024} // 25MB
            multiple
          />

          <div className="flex items-center justify-between border-t pt-4">
            <p className="text-sm text-muted-foreground">
              {attachmentCount}
              {' '}
              file
              {attachmentCount === 1 ? '' : 's'}
              {' '}
              selected
            </p>
            <div className="flex gap-2">
              {attachmentCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearAll}
                >
                  <X className="mr-2 size-4" />
                  Clear All
                </Button>
              )}
              <Button size="sm" onClick={handleAddFiles}>
                Done
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
